import { useEffect, useRef, useState } from "react";
import { dinergatePack, pickClip, spineConfig, spinePacks } from "../../data/spineConfig";
import { useMediaQuery, usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { assetExists } from "../../lib/assets";
import {
  boneStagePos,
  loadGflSpineData,
  loadPngImage,
  loadSpineRuntime,
  playClip,
  sliceSpriteSheet,
  type PixiApp,
  type PixiDisplay,
  type SpineSprite,
} from "../../lib/spine";
import { FallbackWalker } from "./FallbackWalker";
import styles from "./ChibiWalker.module.css";

type Props = {
  enabled: boolean;
};

type Pos = {
  x: number;
  facing: 1 | -1;
  walking: boolean;
};

type Enemy = {
  x: number;
  facing: 1 | -1;
  phase: "dead" | "move" | "dying";
  dieAt: number;
};

type Tracer = {
  live: boolean;
  x: number;
  y: number;
  tx: number;
  ty: number;
  target: number;
};

const WAVE_CAP = spineConfig.waveSize[1];

function emptyEnemies(): Enemy[] {
  return Array.from({ length: WAVE_CAP }, () => ({
    x: -999,
    facing: -1 as const,
    phase: "dead" as const,
    dieAt: 0,
  }));
}

function livingCount(list: Enemy[]) {
  return list.filter((e) => e.phase === "move" || e.phase === "dying").length;
}

function nearestMover(list: Enemy[], heroX: number) {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < list.length; i++) {
    const e = list[i];
    if (!e || e.phase !== "move") continue;
    const dist = Math.abs(e.x - heroX);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function waveGap() {
  const [lo, hi] = spineConfig.waveGapMs;
  return lo + Math.random() * (hi - lo);
}

function waveSize() {
  const [lo, hi] = spineConfig.waveSize;
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

export function ChibiWalker({ enabled }: Props) {
  const pixiHost = useRef<HTMLDivElement>(null);
  const hitRef = useRef<HTMLDivElement>(null);
  const pos = useRef<Pos>({ x: 48, facing: 1, walking: true });
  const enemies = useRef<Enemy[]>(emptyEnemies());
  const attackUntil = useRef(0);
  const nextWaveAt = useRef(0);
  const waveLive = useRef(false);
  const fireReadyAt = useRef(0);
  const flashUntil = useRef(0);
  const flashStart = useRef(0);
  const shotId = useRef(0);
  const shotTarget = useRef(-1);
  const tracer = useRef<Tracer>({ live: false, x: 0, y: 0, tx: 0, ty: 0, target: -1 });
  const [usingSpine, setUsingSpine] = useState(false);
  const reduced = usePrefersReducedMotion();
  const mobile = useMediaQuery("(max-width: 720px)");

  const fireAt = (index: number, now: number) => {
    shotTarget.current = index;
    shotId.current += 1;
    attackUntil.current = now + 720;
    flashStart.current = now;
    flashUntil.current = now + spineConfig.flashMs;
    fireReadyAt.current = now + spineConfig.fireCooldownMs;
    pos.current.walking = false;
  };

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let last = performance.now();
    let idleAt = performance.now() + spineConfig.idleMs[1];
    const speed = mobile ? spineConfig.mobileWalkSpeed : spineConfig.walkSpeed;
    const eSpeed = spineConfig.enemyWalkSpeed;
    nextWaveAt.current = performance.now() + 1600;
    waveLive.current = false;
    enemies.current = emptyEnemies();

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const p = pos.current;
      const list = enemies.current;
      const heroX = p.x + 70;
      const target = nearestMover(list, heroX);
      const inRange = target >= 0 && Math.abs((list[target]?.x ?? 0) - heroX) < spineConfig.engageRange;
      const engaging = inRange || now < attackUntil.current;

      if (reduced) {
        p.walking = false;
      } else if (!engaging) {
        if (now > idleAt) {
          p.walking = !p.walking;
          idleAt = now + (p.walking ? spineConfig.idleMs[1] : spineConfig.idleMs[0]);
        }
        if (p.walking) {
          const max = Math.max(80, window.innerWidth - 130);
          p.x += p.facing * speed * dt;
          if (p.x > max) {
            p.x = max;
            p.facing = -1;
          } else if (p.x < 16) {
            p.x = 16;
            p.facing = 1;
          }
        }
      }

      if (!reduced) {
        if (!waveLive.current && now >= nextWaveAt.current) {
          const n = waveSize();
          const fromRight = heroX < window.innerWidth * 0.55;
          for (let i = 0; i < WAVE_CAP; i++) {
            const slot = list[i];
            if (!slot) continue;
            if (i < n) {
              slot.x = fromRight ? window.innerWidth + 48 + i * 54 : -48 - i * 54;
              slot.facing = fromRight ? -1 : 1;
              slot.phase = "move";
              slot.dieAt = 0;
            } else {
              slot.phase = "dead";
            }
          }
          waveLive.current = true;
        }

        for (const e of list) {
          if (e.phase === "dying" && now >= e.dieAt) {
            e.phase = "dead";
          }
          if (e.phase !== "move") continue;
          const dir: 1 | -1 = e.x < heroX ? 1 : -1;
          e.facing = dir;
          e.x += dir * eSpeed * dt;
        }

        if (waveLive.current && livingCount(list) === 0) {
          waveLive.current = false;
          nextWaveAt.current = now + waveGap();
        }

        if (inRange && target >= 0) {
          const foe = list[target];
          if (foe) {
            p.facing = foe.x > heroX ? 1 : -1;
            p.walking = false;
            if (now >= fireReadyAt.current && !tracer.current.live) {
              fireAt(target, now);
            }
          }
        }
      }

      const hit = hitRef.current;
      if (hit) {
        hit.style.left = `${p.x}px`;
        hit.style.transform = `scaleX(${p.facing})`;
      }
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [enabled, reduced, mobile]);

  useEffect(() => {
    if (!enabled || !pixiHost.current) return;
    const el = pixiHost.current;
    const pack = spinePacks[0];
    if (!pack) return;
    let destroyed = false;
    let app: PixiApp | null = null;
    let offResize: (() => void) | undefined;

    async function boot() {
      const has = await assetExists(pack.skel);
      if (!has || destroyed || !el) return;
      try {
        const pixi = await loadSpineRuntime();
        if (destroyed || !el) return;
        const w = window.innerWidth;
        const h = el.clientHeight || 120;
        app = new pixi.Application({
          width: w,
          height: h,
          transparent: true,
          antialias: true,
        });
        el.replaceChildren(app.view);
        app.view.style.width = "100%";
        app.view.style.height = "100%";
        app.view.style.pointerEvents = "none";

        const { spineData, animations } = await loadGflSpineData(pixi, pack);
        if (destroyed || !app) return;
        const s: SpineSprite = new pixi.spine.Spine(spineData);
        const idle = pickClip(animations, spineConfig.animations.idle) ?? "wait";
        const move = pickClip(animations, spineConfig.animations.move) ?? "move";
        const attack = pickClip(animations, spineConfig.animations.attack) ?? "attack";
        let lastWalk = true;
        let lastAttack = false;
        s.y = h - spineConfig.groundOffset;
        const base = mobile ? spineConfig.mobileScale : spineConfig.desktopScale;
        s.scale.set(base);
        playClip(s, move, true);
        app.stage.addChild(s);

        const foes: SpineSprite[] = [];
        const foePhase: Enemy["phase"][] = [];
        let foeMove = "move";
        let foeDie = "die";
        const eBase = mobile ? spineConfig.enemyMobileScale : spineConfig.enemyDesktopScale;
        const hasEnemy = await assetExists(dinergatePack.skel);
        if (hasEnemy && app && !destroyed) {
          try {
            const loaded = await loadGflSpineData(pixi, dinergatePack);
            if (app && !destroyed) {
              foeMove = pickClip(loaded.animations, spineConfig.animations.move) ?? "move";
              foeDie = pickClip(loaded.animations, spineConfig.animations.die) ?? "die";
              for (let i = 0; i < WAVE_CAP; i++) {
                const foe = new pixi.spine.Spine(loaded.spineData);
                foe.y = h - spineConfig.groundOffset;
                foe.scale.set(eBase);
                foe.visible = false;
                playClip(foe, foeMove, true);
                app.stage.addChild(foe);
                foes.push(foe);
                foePhase.push("dead");
              }
            }
          } catch (err) {
            console.warn("Dinergate Spine failed", err);
          }
        }

        let flash: PixiDisplay | null = null;
        let frames: unknown[] = [];
        const hasFlash = await assetExists(spineConfig.muzzleFlash.src);
        if (hasFlash && app && !destroyed) {
          try {
            const img = await loadPngImage(spineConfig.muzzleFlash.src);
            frames = sliceSpriteSheet(pixi, img, spineConfig.muzzleFlash.cols, spineConfig.muzzleFlash.rows);
            const first = frames[0];
            if (first) {
              flash = new pixi.Sprite(first);
              flash.anchor?.set(0.06, 0.5);
              flash.scale.set(spineConfig.muzzleFlash.scale);
              flash.visible = false;
              if (pixi.BLEND_MODES) flash.blendMode = pixi.BLEND_MODES.ADD;
              app.stage.addChild(flash);
            }
          } catch (err) {
            console.warn("Muzzle flash sheet failed", err);
          }
        }

        const bolt = new pixi.Graphics();
        bolt.beginFill(0xffe566);
        bolt.drawCircle(0, 0, 5);
        bolt.endFill();
        bolt.visible = false;
        app.stage.addChild(bolt);

        let lastShot = 0;
        let lastFx = performance.now();
        setUsingSpine(true);
        app.ticker.add(() => {
          const now = performance.now();
          const dt = Math.min(0.05, (now - lastFx) / 1000);
          lastFx = now;
          const p = pos.current;
          const list = enemies.current;
          s.x = p.x + 70;
          s.scale.x = base * p.facing;
          s.scale.y = base;
          const attacking = now < attackUntil.current;
          if (attacking && !lastAttack) {
            lastAttack = true;
            playClip(s, attack, false);
          } else if (!attacking && lastAttack) {
            lastAttack = false;
            lastWalk = p.walking;
            playClip(s, p.walking ? move : idle, true);
          } else if (!attacking && p.walking !== lastWalk) {
            lastWalk = p.walking;
            playClip(s, p.walking ? move : idle, true);
          }

          for (let i = 0; i < foes.length; i++) {
            const foe = foes[i];
            const e = list[i];
            if (!foe || !e) continue;
            foe.visible = e.phase !== "dead";
            foe.x = e.x;
            foe.scale.x = eBase * -e.facing;
            foe.scale.y = eBase;
            if (e.phase !== foePhase[i]) {
              foePhase[i] = e.phase;
              if (e.phase === "move") playClip(foe, foeMove, true);
              else if (e.phase === "dying") playClip(foe, foeDie, false);
            }
          }

          if (shotId.current !== lastShot) {
            lastShot = shotId.current;
            const muzzle = boneStagePos(s, "muzzle") ?? {
              x: s.x + 46 * p.facing,
              y: s.y - 54,
            };
            const ti = shotTarget.current;
            const targetSprite = foes[ti];
            const body = targetSprite
              ? (boneStagePos(targetSprite, "body") ?? { x: targetSprite.x, y: targetSprite.y - 22 })
              : { x: muzzle.x + 180 * p.facing, y: s.y - 18 };
            tracer.current = {
              live: true,
              x: muzzle.x,
              y: muzzle.y,
              tx: body.x,
              ty: body.y,
              target: ti,
            };
          }

          if (flash) {
            const flashing = now < flashUntil.current;
            flash.visible = flashing;
            if (flashing) {
              const muzzle = boneStagePos(s, "muzzle") ?? {
                x: s.x + 46 * p.facing,
                y: s.y - 54,
              };
              flash.x = muzzle.x;
              flash.y = muzzle.y;
              const mag = spineConfig.muzzleFlash.scale;
              flash.scale.x = mag * p.facing;
              flash.scale.y = mag;
              const t = (now - flashStart.current) / spineConfig.flashMs;
              const frame = Math.min(frames.length - 1, Math.max(0, Math.floor(t * frames.length)));
              const tex = frames[frame];
              if (tex) flash.texture = tex;
            }
          }

          const tr = tracer.current;
          if (tr.live) {
            const dx = tr.tx - tr.x;
            const dy = tr.ty - tr.y;
            const dist = Math.hypot(dx, dy);
            const step = spineConfig.tracerSpeed * dt;
            if (dist <= step || dist < 2) {
              bolt.visible = false;
              tr.live = false;
              const hit = list[tr.target];
              if (hit && hit.phase === "move") {
                hit.phase = "dying";
                hit.dieAt = now + spineConfig.dieMs;
              }
            } else {
              tr.x += (dx / dist) * step;
              tr.y += (dy / dist) * step;
              bolt.x = tr.x;
              bolt.y = tr.y;
              bolt.visible = true;
            }
          } else {
            bolt.visible = false;
          }
        });
      } catch (err) {
        console.warn("Chibi walker Spine failed", err);
        setUsingSpine(false);
      }

      const onResize = () => {
        app?.renderer.resize(window.innerWidth, el.clientHeight || 120);
      };
      window.addEventListener("resize", onResize);
      offResize = () => window.removeEventListener("resize", onResize);
    }

    void boot();
    return () => {
      destroyed = true;
      offResize?.();
      app?.destroy(true, true);
    };
  }, [enabled, mobile]);

  if (!enabled) return null;

  const onClick = () => {
    const now = performance.now();
    const heroX = pos.current.x + 70;
    const target = nearestMover(enemies.current, heroX);
    const inRange = target >= 0 && Math.abs((enemies.current[target]?.x ?? 0) - heroX) < spineConfig.engageRange;
    if (inRange) {
      fireAt(target, now);
      return;
    }
    attackUntil.current = now + 800;
    pos.current.walking = false;
  };

  return (
    <div className={styles.layer}>
      <div ref={pixiHost} className={styles.pixiHost} />
      <div ref={hitRef} className={styles.hit} style={{ left: 48 }} onClick={onClick}>
        {!usingSpine ? (
          <FallbackWalker
            width={120}
            height={140}
            x={0}
            facing={1}
            walking={!reduced}
            onClick={onClick}
          />
        ) : null}
      </div>
      {!usingSpine ? <p className={styles.ph}>Placeholder patrol — Spine 2.1 pack did not load</p> : null}
    </div>
  );
}
